/* eslint-disable @typescript-eslint/no-explicit-any */
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, CheckCircle2, Cloud, CloudOff, Database, ExternalLink, FileStack, History, LayoutGrid, Layers, Loader2, Plus, Redo2, Rocket, Sparkles, Undo2, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { adminApi } from "../api/admin";
import { ApiError, apiRequest } from "../api/client";
import { ownerApi } from "../api/owner";
import type { PresenceUser, ReportDraft } from "../types";
import { Button, Drawer, useToast } from "../ds";
import { PublishDialog } from "../pages/v2/ReportWorkspacePage";
import type { ReportTemplate } from "../pages/v2/templates";
import { PreviewThemeToggle, usePreviewTheme } from "../shell/TvFrameTheme";
import { specFor } from "./catalog";
import { TemplatePickerModal } from "./EmptyCanvas";
import { ensureNumberMeasure, type FieldDrag } from "./autoMeasure";
import { DataPane } from "./DataPane";
import { PagesPanel, VisualsPanel } from "./LeftPanels";
import { LayersPanel } from "./LayersPanel";
import { ShortcutsModal } from "./ShortcutsModal";
import { copyFormat, pasteFormat, type FormatClip } from "./formatPainter";
import { moveRelative, restack, topZ, type StackOp } from "./zorder";
import { findFreeSpot, isBarWidget, newWidget, pageIsFull, rowsFor, uid, useHistory, type SDef, type SLayout, type SPage, type SWidget } from "./model";
import { setIn, type ApplyDisplayPatch } from "./FormatControls";
import { DesignScoreToolbarItem } from "./DesignScore"; // workstream D
import { AiChatPanel } from "./AiChatPanel";
import { VersionHistoryPanel } from "./HistoryPanel";
import { GuideButton } from "./GuidePanel";
import { guideSteps, type GuideFlags, type GuideStepId } from "./guide";
import { pickChart } from "./chartFromField";
import { ArrangeBar } from "./ArrangeBar";
import { scaleOf } from "./layoutTools";
import { merge3 } from "./merge";
import { applyEditToSelection } from "./multiEdit";
import { PresenceBar } from "./PresenceBar";
import { PageProperties, PropertiesPanel } from "./PropertiesPanel";
import { StudioCanvas, type CanvasHandle } from "./StudioCanvas";
import { takesNumberFormat, type NumberFormat } from "./numberFormat";
// Workstream D owns SuggestPanel.tsx (contract v2.1 section 7); B ships a placeholder with the same export.
import { SuggestPanel } from "./SuggestPanel";
import "./studio.css";

type Left = "visuals" | "data" | "pages" | "layers";
type Save = "saved" | "dirty" | "saving" | "error";

export function StudioPage({ mode }: { mode: "admin" | "owner" }) {
  const { reportId = "" } = useParams();
  const api = mode === "admin" ? adminApi : ownerApi;
  const base = mode === "admin" ? "/admin" : "/owner";
  const navigate = useNavigate();
  const toast = useToast();
  const qc = useQueryClient();
  const canvasRef = useRef<CanvasHandle>(null);

  const draftQ = useQuery({ queryKey: ["studio-draft", mode, reportId], queryFn: () => api.getDraft(reportId), refetchOnWindowFocus: false, staleTime: Infinity });
  const history = useHistory(null);
  const def = history.definition as SDef | null;
  const [pageId, setPageId] = useState<string>("");
  const [selected, setSelectedRaw] = useState<string | null>(null);
  // Shift-click adds to the selection; `selected` stays the primary (first) one.
  const [extra, setExtra] = useState<string[]>([]);
  const setSelected = useCallback((id: string | null) => {
    setSelectedRaw(id);
    setExtra([]);
  }, []);
  const selectMany = useCallback((ids: string[]) => {
    setSelectedRaw(ids[0] ?? null);
    setExtra(ids.slice(1));
  }, []);
  const toggleSelected = useCallback(
    (id: string) => {
      if (!selected) return setSelected(id);
      if (id === selected) {
        setSelectedRaw(extra[0] ?? null);
        setExtra(extra.slice(1));
      } else setExtra((e) => (e.includes(id) ? e.filter((x) => x !== id) : [...e, id]));
    },
    [selected, extra, setSelected],
  );
  const [left, setLeft] = useState<Left>("visuals");
  const [dataDs, setDataDs] = useState<string | null>(null);
  const [save, setSave] = useState<Save>("saved");
  const [saveMsg, setSaveMsg] = useState<string>("");
  const [publishOpen, setPublishOpen] = useState(false);
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [templateOpen, setTemplateOpen] = useState(false);
  const [flashVisuals, setFlashVisuals] = useState(false);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  // Guide checklist: steps 3-5 are things the person did, remembered per report in this browser.
  const [guideFlags, setGuideFlags] = useState<GuideFlags>(() => {
    try {
      return JSON.parse(window.localStorage.getItem(`mrp-guide-v1-${reportId}`) || "{}") as GuideFlags;
    } catch {
      return {};
    }
  });
  const markGuide = useCallback(
    (k: keyof GuideFlags) => {
      setGuideFlags((g) => {
        if (g[k]) return g;
        const n = { ...g, [k]: true };
        try {
          window.localStorage.setItem(`mrp-guide-v1-${reportId}`, JSON.stringify(n));
        } catch {
          /* the checklist just forgets it next time */
        }
        return n;
      });
    },
    [reportId],
  );
  const openedTheme = useRef<string | undefined>(undefined);
  // Bumped to open the Data tab's "Add data" picker from the Guide / Start-here flow.
  const [pickerSignal, setPickerSignal] = useState(0);
  // F2: the Layers pane starts renaming this visual.
  const [renameId, setRenameId] = useState<string | null>(null);
  // Format painter clipboard (kept while you move between pages).
  const [formatClip, setFormatClip] = useState<FormatClip | null>(null);
  // "Ask AI" chat panel + its dismissible first-run hint (remembered per browser).
  const [aiOpen, setAiOpen] = useState(false);
  const [aiHint, setAiHint] = useState<boolean>(() => {
    try {
      return window.localStorage.getItem("mrp-ai-hint-v1") !== "1";
    } catch {
      return false;
    }
  });
  const dismissAiHint = useCallback(() => {
    setAiHint(false);
    try {
      window.localStorage.setItem("mrp-ai-hint-v1", "1");
    } catch {
      /* hint just shows again next time */
    }
  }, []);
  /** The definition the last AI edit produced, so "Undo" only fires when nothing was edited since. */
  const aiResultRef = useRef<SDef | null>(null);
  const [canvasTheme, setCanvasTheme] = usePreviewTheme();
  const revision = useRef<number>(0);
  // Always the current draft, for work that finishes after an await (DataPane adds links later).
  const latestDef = useRef<SDef | null>(null);
  const persisted = useRef<string>("");
  const saving = useRef(false);
  const queued = useRef(false);
  // The draft as the server last had it for this tab: the common ancestor for
  // merging other people's saves with local edits (see merge.ts).
  const baseRef = useRef<SDef | null>(null);
  const clientId = useRef<string>(uid("tab"));
  const [peers, setPeers] = useState<PresenceUser[]>([]);
  const [remoteNote, setRemoteNote] = useState<string>("");
  const pageIdRef = useRef<string>("");
  const selectedRef = useRef<string | null>(null);
  const saveRef = useRef<Save>("saved");
  const peersRef = useRef<PresenceUser[]>([]);
  const adoptRef = useRef<(latest: ReportDraft, who?: string | null) => void>(() => undefined);

  // Load once.
  useEffect(() => {
    if (!draftQ.data || def) return;
    const d = draftQ.data.definition_json as unknown as SDef;
    history.reset(d);
    revision.current = draftQ.data.revision_number;
    persisted.current = JSON.stringify(d);
    baseRef.current = d;
    openedTheme.current = d.theme;
    setPageId(d.defaultPageId || d.pages?.[0]?.id);
  }, [draftQ.data, def, history]);

  latestDef.current = (def as SDef | null) ?? null;
  pageIdRef.current = pageId;
  selectedRef.current = selected;
  saveRef.current = save;
  peersRef.current = peers;

  // Fold the server's newer draft (someone else's save) into this tab: their
  // changes appear, local unsaved edits stay and are saved on top.
  const adoptServer = useCallback(
    (latest: ReportDraft, who?: string | null) => {
      const theirs = latest.definition_json as unknown as SDef;
      const baseDef = baseRef.current ?? theirs;
      const local = latestDef.current;
      const result = local ? merge3(baseDef, local, theirs) : null;
      history.rebase((d) => merge3(baseDef, d, theirs).merged);
      baseRef.current = theirs;
      persisted.current = JSON.stringify(theirs);
      revision.current = latest.revision_number;
      const name = who || "someone";
      setRemoteNote(`Updated with ${name}'s changes`);
      window.setTimeout(() => setRemoteNote(""), 6000);
      if (result && result.conflicts.length > 0) {
        toast.error(
          `Merged ${name}'s changes - ${result.conflicts.length} setting${result.conflicts.length === 1 ? "" : "s"} you both changed kept your value`,
          result.conflicts.slice(0, 4).join("; "),
        );
      }
      canvasRef.current?.post({ type: "mrp:refetch" });
    },
    [history, toast],
  );
  adoptRef.current = adoptServer;
  const page: SPage | undefined = useMemo(() => def?.pages.find((p) => p.id === pageId) ?? def?.pages[0], [def, pageId]);
  const widget: SWidget | undefined = page?.widgets.find((w) => w.id === selected);
  const selectedIds = selected ? [selected, ...extra].filter((id) => page?.widgets.some((w) => w.id === id)) : [];
  const gridScale = page ? scaleOf(page) : 1;
  const selKey = selectedIds.join("|");

  // ---- live canvas: push every change to the renderer immediately ----
  useEffect(() => {
    if (!def) return;
    const t = window.setTimeout(() => canvasRef.current?.post({ type: "mrp:definition", definition: def }), 90);
    return () => window.clearTimeout(t);
  }, [def]);

  // ---- autosave ----
  const doSave = useCallback(async () => {
    const current = history.definition as SDef | null;
    if (!current) return;
    const body = JSON.stringify(current);
    if (body === persisted.current) {
      setSave("saved");
      return;
    }
    if (saving.current) {
      queued.current = true;
      return;
    }
    saving.current = true;
    setSave("saving");
    try {
      const res = await api.saveDraft(reportId, { definition: current as unknown as Record<string, unknown>, expectedRevision: revision.current });
      revision.current = res.revision_number;
      persisted.current = body;
      baseRef.current = current;
      setSave("saved");
      setSaveMsg(`Saved ${new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`);
      canvasRef.current?.post({ type: "mrp:refetch" });
      qc.invalidateQueries({ queryKey: ["draft"] });
      // The server renames the report when the Report name changes here.
      qc.invalidateQueries({ queryKey: ["report"] });
      qc.invalidateQueries({ queryKey: ["reports"] });
    } catch (e) {
      let merged = false;
      if (e instanceof ApiError && e.status === 409) {
        // The draft moved on (another person/tab/AI save, or our own save whose
        // reply was lost). Never discard anyone's work: merge their version with
        // ours and save the result.
        try {
          const latest = await api.getDraft(reportId);
          adoptRef.current(latest, peersRef.current.find((p) => !p.is_me)?.name ?? null);
          setSave("dirty");
          merged = true;
        } catch {
          /* fall through to the error message */
        }
      }
      if (!merged) {
        setSave("error");
        setSaveMsg(e instanceof Error ? e.message : "Save failed - will retry.");
      }
    } finally {
      saving.current = false;
      if (queued.current) {
        queued.current = false;
        void doSave();
      }
    }
  }, [api, reportId, history.definition, qc]);

  useEffect(() => {
    if (!def) return;
    if (JSON.stringify(def) === persisted.current) return;
    setSave("dirty");
    const t = window.setTimeout(() => void doSave(), 1100);
    return () => window.clearTimeout(t);
  }, [def, doSave]);

  // ---- presence + live sync with other people's saves ----
  const loaded = !!def;
  useEffect(() => {
    if (!loaded) return;
    let alive = true;
    const cid = clientId.current;
    const tick = async () => {
      if (!alive || document.hidden) return;
      try {
        const r = await api.presence(reportId, {
          client_id: cid,
          page_id: pageIdRef.current || null,
          widget_id: selectedRef.current,
          state: saveRef.current === "dirty" || saveRef.current === "saving" ? "editing" : "viewing",
        });
        if (!alive) return;
        setPeers(Array.isArray(r.users) ? r.users : []);
        if (typeof r.revision_number === "number" && r.revision_number > revision.current && !saving.current) {
          const latest = await api.getDraft(reportId);
          if (alive && latest.revision_number > revision.current && !saving.current) adoptRef.current(latest, r.updated_by_name);
        }
      } catch {
        /* offline / restarting: try again next tick */
      }
    };
    void tick();
    const timer = window.setInterval(() => void tick(), 4000);
    return () => {
      alive = false;
      window.clearInterval(timer);
      void api.leavePresence(reportId, cid).catch(() => undefined);
    };
  }, [loaded, api, reportId]);

  useEffect(() => {
    const h = (e: BeforeUnloadEvent) => {
      if (save === "dirty" || save === "saving") {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [save]);

  // ---- editing helpers ----
  const commit = useCallback((next: SDef, key?: string) => history.commit(next, key), [history]);
  const patchPage = useCallback(
    (pid: string, fn: (p: SPage) => SPage, key?: string) => {
      if (!def) return;
      commit({ ...def, pages: def.pages.map((p) => (p.id === pid ? fn(p) : p)) }, key);
    },
    [def, commit],
  );
  const patchWidget = useCallback(
    (wid: string, patch: Partial<SWidget>, key?: string) => page && patchPage(page.id, (p) => ({ ...p, widgets: p.widgets.map((w) => (w.id === wid ? { ...w, ...patch } : w)) }), key),
    [page, patchPage],
  );

  /** Properties/Format pane edit: with 2+ visuals selected it applies to ALL of them as ONE undo step. */
  const patchSelection = useCallback(
    (patch: Partial<SWidget>, key?: string) => {
      if (!page || !widget) return;
      if (selectedIds.length > 1) patchPage(page.id, (p) => applyEditToSelection(p, selectedIds, widget.id, patch).page, key);
      else patchWidget(widget.id, patch, key);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [page, widget, selKey, patchPage, patchWidget],
  );

  /** A confirmation with a one-click Undo (the same as Ctrl+Z). */
  const undoToast = useCallback(
    (title: string, message?: string) => toast.show({ tone: "success", title, message, action: { label: "Undo", onClick: () => history.undo() } }),
    [toast, history],
  );

  /** "Add chart from this field": the designer picks the visual (see chartFromField.ts). */
  const chartFromField = useCallback(
    async (f: FieldDrag, at?: { x: number; y: number }) => {
      if (!page) return;
      const base = mode === "admin" ? "/api/v1/admin/datasets" : "/api/v1/owner/datasets";
      try {
        const [fields, measures] = await Promise.all([
          qc.fetchQuery({ queryKey: ["studio-fields", mode, f.datasetId], queryFn: () => apiRequest<any[]>(`${base}/${f.datasetId}/fields`), staleTime: 5 * 60_000 }),
          qc.fetchQuery({ queryKey: ["studio-measures", mode, f.datasetId], queryFn: () => apiRequest<any[]>(`${base}/${f.datasetId}/measures`), staleTime: 5 * 60_000 }),
        ]);
        const col = fields.find((x: any) => x.source_name === f.name);
        const numeric = f.numeric ?? (f.kind === "dimension" && col?.field_role === "measure");
        const choice = pickChart(
          { name: f.name, numeric, isMeasure: f.kind === "measure", dataType: f.dataType ?? col?.data_type, label: f.label ?? col?.display_name },
          {
            measures,
            numericFields: fields.filter((x: any) => x.field_role === "measure" && x.is_visible !== false).map((x: any) => ({ source_name: x.source_name, display_name: x.display_name })),
          },
        );
        let measure: string | null = null;
        if (choice.measure?.kind === "existing") measure = choice.measure.name;
        else if (choice.measure?.kind === "total-of") {
          const tm = choice.measure;
          const c = fields.find((x: any) => x.source_name === tm.field);
          measure = await ensureNumberMeasure(qc, mode, { kind: "dimension", name: tm.field, datasetId: f.datasetId, numeric: true, label: tm.label, aggregation: c?.default_aggregation });
        }
        const spec = specFor(choice.type);
        const k = scaleOf(page);
        const size = { w: (spec?.size ?? { w: 8, h: 8 }).w * k, h: (spec?.size ?? { w: 8, h: 8 }).h * k };
        const cols = page.canvas?.columns ?? 24;
        const layout: SLayout = at ? { x: Math.max(0, Math.min(at.x, cols - size.w)), y: at.y, w: size.w, h: size.h } : findFreeSpot(page, size.w, size.h);
        const w = newWidget(choice.type, f.label ?? col?.display_name ?? spec?.label ?? choice.type, { ...layout, z: topZ(page) });
        w.datasetId = f.datasetId;
        w.query = {
          ...(w.query ?? {}),
          dimensions: choice.dimensions,
          measures: measure ? [measure] : [],
          sort: measure && choice.type === "bar_chart" ? [{ field: measure, direction: "desc" }] : choice.type === "line_chart" ? [{ field: choice.dimensions[0], direction: "asc" }] : [],
          limit: choice.type === "bar_chart" ? 12 : 100,
        };
        patchPage(page.id, (p) => ({ ...p, widgets: [...p.widgets, w] }));
        setDataDs(f.datasetId);
        setSelected(w.id);
        undoToast(`Added ${spec?.label?.toLowerCase() ?? "a chart"}`, choice.why);
      } catch (e) {
        toast.error("Couldn't add a chart", e instanceof Error ? e.message : undefined);
      }
    },
    [page, mode, qc, patchPage, undoToast, toast, setSelected],
  );

  const previewAsTv = useCallback(() => {
    void doSave();
    markGuide("preview");
    window.open(`/tv-preview/preview.html?reportId=${reportId}${mode === "owner" ? "&mode=owner" : ""}&theme=${canvasTheme}`, "_blank", "noopener");
  }, [doSave, markGuide, reportId, mode, canvasTheme]);
  const guideAction = useCallback(
    (id: GuideStepId) => {
      if (id === "data") {
        setLeft("data");
        setPickerSignal((n) => n + 1);
      } else if (id === "visual") setSuggestOpen(true);
      else if (id === "style") {
        setSelected(null);
        markGuide("style");
        toast.info("Pick a Look on the right", "With nothing selected, the pane on the right shows the report Look (colours and fonts) and page settings.");
      } else if (id === "preview") previewAsTv();
      else {
        markGuide("save");
        void doSave().then(() => setPublishOpen(true));
      }
    },
    [setSelected, markGuide, toast, previewAsTv, doSave],
  );

  // ---- Ask AI ----
  const aiSnapshot = useCallback(() => (latestDef.current ? { def: latestDef.current, pageId: pageIdRef.current } : null), []);
  /** The AI worked on `sent` for 20-60 s: merge its result with whatever changed meanwhile
   * (my edits, a co-editor's), then commit as ONE history step (never coalesced with typing). */
  const applyAi = useCallback(
    (sent: SDef, aiDef: SDef, count: number) => {
      const current = latestDef.current ?? sent;
      const merged = merge3(sent, current, aiDef).merged as SDef;
      aiResultRef.current = merged;
      commit(merged, uid("ai-edit"));
      if (!merged.pages.some((p) => p.id === pageIdRef.current)) setPageId(merged.pages[0]?.id ?? "");
      const stillThere = (id: string | null) => !!id && merged.pages.some((p) => p.widgets.some((w) => w.id === id));
      if (selectedRef.current && !stillThere(selectedRef.current)) setSelected(null);
      toast.success(`AI changed ${count} thing${count === 1 ? "" : "s"}`, "Ctrl+Z undoes it. It's a draft - nothing is published.");
    },
    [commit, toast, setSelected],
  );
  const undoAi = useCallback((): boolean => {
    if (aiResultRef.current && latestDef.current === aiResultRef.current) {
      aiResultRef.current = null;
      history.undo();
      return true;
    }
    toast.info("You've edited since then", "Use Ctrl+Z to step back through your changes.");
    return false;
  }, [history, toast]);

  /** Format pane "Use on every visual on this page": one undo step. */
  const applyNumberFormatToPage = useCallback(
    (nf: NumberFormat) => {
      if (!page) return;
      let count = 0;
      patchPage(page.id, (p) => ({
        ...p,
        widgets: p.widgets.map((w) => {
          if (!takesNumberFormat(w.type)) return w;
          count += 1;
          const display: Record<string, unknown> = { ...(w.display ?? {}), numberFormat: { ...nf } };
          delete display.format;
          return { ...w, display };
        }),
      }));
      toast.success(`Number format applied to ${count} visual${count === 1 ? "" : "s"}`, "Ctrl+Z undoes it.");
    },
    [page, patchPage, toast],
  );

  /** Format pane "Apply to every ... on this page / in this report": ONE undo step. A plain-object value
   *  is merged key by key, so an undefined key removes that setting. */
  const applyDisplayPatch = useCallback<ApplyDisplayPatch>(
    (path, value, scope, onlyTypes) => {
      if (!def || !page) return;
      const isObj = value !== null && typeof value === "object" && !Array.isArray(value);
      let count = 0;
      const apply = (w: SWidget): SWidget => {
        if (onlyTypes && !onlyTypes.includes(w.type)) return w;
        let display = (w.display ?? {}) as Record<string, any>;
        if (isObj) for (const [k, v] of Object.entries(value as Record<string, unknown>)) display = setIn(display, [...path, k], v);
        else display = setIn(display, path, value);
        count += 1;
        return { ...w, display };
      };
      commit({ ...def, pages: def.pages.map((p) => (scope === "report" || p.id === page.id ? { ...p, widgets: p.widgets.map(apply) } : p)) }, `apply-display-${path.join(".")}`);
      toast.success(`Applied to ${count} visual${count === 1 ? "" : "s"}`, scope === "report" ? "Across the whole report. Ctrl+Z undoes it." : "On this page. Ctrl+Z undoes it.");
    },
    [def, page, commit, toast],
  );

  const addVisual = useCallback(
    (type: string, at?: { x: number; y: number }) => {
      if (!page) return;
      const spec = specFor(type);
      const k = scaleOf(page);
      const size = { w: (spec?.size ?? { w: 6, h: 6 }).w * k, h: (spec?.size ?? { w: 6, h: 6 }).h * k };
      const cols = page.canvas?.columns ?? 24;
      const full = !at && pageIsFull(page, size.w, size.h);
      const layout: SLayout = at ? { x: Math.min(at.x, cols - size.w), y: at.y, w: size.w, h: size.h } : findFreeSpot(page, size.w, size.h);
      if (full) toast.info("This page is full", "The new visual sits on top of others — drag it into place, resize something, or add a new page.");
      const w = newWidget(type, spec?.label ?? type, { ...layout, z: topZ(page) });
      if (dataDs && spec?.needsData) w.datasetId = dataDs;
      if (type === "text") w.display = { ...w.display, text: "Add your text" };
      if (type === "html") w.display = { ...w.display, html: '<div class="panel"><div class="label">Total</div><div class="value">{{value}}</div></div>', css: ".panel{padding:16px}.label{opacity:.7}.value{font-size:40px;font-weight:700}" };
      patchPage(page.id, (p) => ({ ...p, widgets: [...p.widgets, w] }));
      setSelected(w.id);
    },
    [page, patchPage, dataDs, toast],
  );

  const removeWidget = useCallback(
    (wid: string) => {
      if (!page) return;
      if (page.widgets.find((w) => w.id === wid)?.locked) {
        toast.info("This visual is locked", "Unlock it in the Layers tab to delete it.");
        return;
      }
      patchPage(page.id, (p) => ({ ...p, widgets: p.widgets.filter((w) => w.id !== wid) }));
      setSelected(null);
      toast.show({ tone: "info", title: "Visual removed", message: "Press Ctrl+Z to bring it back.", action: { label: "Undo", onClick: () => history.undo() } });
    },
    [page, patchPage, toast, history],
  );
  const duplicateWidget = useCallback(
    (wid: string) => {
      if (!page) return;
      const src = page.widgets.find((w) => w.id === wid);
      if (!src) return;
      const spot = findFreeSpot(page, src.layout.w, src.layout.h);
      const copy = { ...structuredClone(src), id: uid("w"), layout: { ...src.layout, x: spot.x, y: spot.y, z: topZ(page) }, locked: false };
      patchPage(page.id, (p) => ({ ...p, widgets: [...p.widgets, copy] }));
      setSelected(copy.id);
    },
    [page, patchPage],
  );

  /** A ready-made widget (SuggestPanel) placed in the first free spot. */
  const addReadyWidget = useCallback(
    (src: SWidget) => {
      if (!page) return;
      const w = { ...structuredClone(src), id: uid("w") } as SWidget;
      const k = scaleOf(page);
      const size = { w: (w.layout?.w ?? 8) * k, h: (w.layout?.h ?? 8) * k };
      w.layout = { ...findFreeSpot(page, size.w, size.h), z: topZ(page) };
      patchPage(page.id, (p) => ({ ...p, widgets: [...p.widgets, w] }));
      setSelected(w.id);
    },
    [page, patchPage],
  );

  /** Put a template's layout on the current (empty) page; extra template pages are appended. */
  const applyTemplate = useCallback(
    (t: ReportTemplate) => {
      if (!def || !page) return;
      const mk = (tw: ReportTemplate["pages"][number]["widgets"][number], z: number): SWidget => {
        const spec = specFor(tw.type);
        const k = scaleOf(page);
        const w = newWidget(tw.type, tw.title, { x: tw.x * k, y: tw.y * k, w: tw.w * k, h: tw.h * k, z: z + 1 });
        if (dataDs && spec?.needsData) w.datasetId = dataDs;
        return { ...w, ...(tw.extra ?? {}) } as SWidget;
      };
      const [first, ...rest] = t.pages;
      const extra: SPage[] = rest.map((tp, i) => ({ id: `page-${Math.random().toString(36).slice(2, 7)}`, name: tp.name, order: def.pages.length + i, widgets: tp.widgets.map(mk), canvas: page.canvas, background: (page as any).background, filters: [] }));
      commit({
        ...def,
        pages: [...def.pages.map((p) => (p.id === page.id ? { ...p, name: p.widgets.length || !/^Page \d+$|^Overview$/.test(p.name) ? p.name : first.name, widgets: [...p.widgets, ...first.widgets.map(mk)] } : p)), ...extra],
      });
      setTemplateOpen(false);
      setSelected(null);
      toast.success(`“${t.name}” layout added`, dataDs ? "Select each visual and pick its fields in the Data tab." : "Select each visual, then choose a dataset and fields for it.");
    },
    [def, page, commit, dataDs, toast],
  );

  const bindField = useCallback(
    async (wid: string, dropped: FieldDrag) => {
      const w = page?.widgets.find((x) => x.id === wid);
      if (!w) return;
      const spec = specFor(w.type);
      let f: FieldDrag = dropped;
      if (dropped.numeric && dropped.kind === "dimension" && spec?.wells.values) {
        try {
          f = { kind: "measure", name: await ensureNumberMeasure(qc, mode, dropped), datasetId: dropped.datasetId };
        } catch (e) {
          toast.error("Couldn't add the total", e instanceof Error ? e.message : undefined);
          return;
        }
      }
      const q = { ...(w.query ?? {}) };
      const sameDs = w.datasetId === f.datasetId;
      const dims = sameDs ? [...(q.dimensions ?? [])] : [];
      const meas = sameDs ? [...(q.measures ?? [])] : [];
      if (f.kind === "measure") {
        if (!meas.includes(f.name)) (spec?.wells.maxValues === 1 ? meas.splice(0, meas.length, f.name) : meas.push(f.name));
      } else if (spec?.wells.field) dims.splice(0, dims.length, f.name);
      else if (!dims.includes(f.name)) dims.push(f.name);
      patchWidget(wid, { datasetId: f.datasetId, query: { ...q, dimensions: dims, measures: meas } });
      setSelected(wid);
    },
    [page, patchWidget, qc, mode, toast],
  );

  // ---- stacking, hide/lock, format painter ----
  const restackSelection = useCallback(
    (op: StackOp) => {
      if (!page || selectedIds.length === 0) return;
      const next = restack(page, selectedIds, op);
      if (next === page) {
        toast.info(op === "front" || op === "forward" ? "Already in front" : "Already at the back");
        return;
      }
      patchPage(page.id, () => next);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [page, selKey, patchPage, toast],
  );
  const toggleFlag = useCallback(
    (flag: "hidden" | "locked") => {
      if (!page || selectedIds.length === 0) return;
      const ids = new Set(selectedIds);
      const targets = page.widgets.filter((w) => ids.has(w.id));
      const on = !targets.every((w) => !!w[flag]);
      patchPage(page.id, (p) => ({ ...p, widgets: p.widgets.map((w) => (ids.has(w.id) ? { ...w, [flag]: on } : w)) }));
      toast.info(`${targets.length} visual${targets.length === 1 ? "" : "s"} ${flag === "hidden" ? (on ? "hidden from viewers" : "shown again") : on ? "locked" : "unlocked"}`);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [page, selKey, patchPage, toast],
  );
  const copyFormatCmd = useCallback(() => {
    if (!widget) return;
    setFormatClip(copyFormat(widget));
    toast.info("Format copied", `From "${widget.title || widget.type}". Select other visuals and press Ctrl+Shift+V.`);
  }, [widget, toast]);
  const pasteFormatCmd = useCallback(() => {
    if (!page || !formatClip) {
      toast.info("Nothing copied yet", "Select a visual and use Copy format first.");
      return;
    }
    if (selectedIds.length === 0) return;
    const res = pasteFormat(page, selectedIds, formatClip);
    if (res.changed === 0) {
      toast.info("Nothing to change", "The selected visuals already look like that.");
      return;
    }
    patchPage(page.id, () => res.page);
    toast.success(`Format applied to ${res.changed} visual${res.changed === 1 ? "" : "s"}`, `${res.partial ? `${res.partial} of a different kind got the shared look only (title, card, border). ` : ""}Ctrl+Z undoes it.`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, formatClip, selKey, patchPage, toast]);

  // ---- keyboard ----
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement;
      const typing = t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.tagName === "SELECT" || t.isContentEditable);
      const mod = e.ctrlKey || e.metaKey;
      if (mod && e.key === "/") {
        e.preventDefault();
        setAiOpen((o) => !o);
        setAiHint(false);
        return;
      }
      if (mod && e.key.toLowerCase() === "z" && !typing) {
        e.preventDefault();
        if (e.shiftKey) history.redo();
        else history.undo();
        return;
      }
      if (mod && e.key.toLowerCase() === "y" && !typing) {
        e.preventDefault();
        history.redo();
        return;
      }
      if (mod && e.key.toLowerCase() === "s") {
        e.preventDefault();
        void doSave();
        return;
      }
      if (!typing && page && mod && e.key.toLowerCase() === "a") {
        e.preventDefault();
        const all = page.widgets.filter((w) => !isBarWidget(w)).map((w) => w.id);
        setSelectedRaw(all[0] ?? null);
        setExtra(all.slice(1));
        return;
      }
      if (!typing && !mod && !e.altKey && e.key === "?") {
        e.preventDefault();
        setShortcutsOpen(true);
        return;
      }
      if (!typing && e.key === "F2" && widget) {
        e.preventDefault();
        setLeft("layers");
        setRenameId(widget.id);
        return;
      }
      if (!typing && page && mod && (e.code === "BracketRight" || e.code === "BracketLeft") && selectedIds.length) {
        e.preventDefault();
        restackSelection(e.code === "BracketRight" ? (e.shiftKey ? "front" : "forward") : e.shiftKey ? "back" : "backward");
        return;
      }
      if (!typing && page && mod) {
        const k = e.key.toLowerCase();
        const painter = (e.shiftKey || e.altKey) && (k === "c" || k === "v");
        const flag = e.shiftKey && !e.altKey && (k === "h" || k === "l");
        if (painter || flag) {
          e.preventDefault();
          if (k === "c") copyFormatCmd();
          else if (k === "v") pasteFormatCmd();
          else if (selectedIds.length) toggleFlag(k === "h" ? "hidden" : "locked");
          return;
        }
      }
      if (typing || !widget || !page) return;
      if (e.key === "Delete" || e.key === "Backspace") {
        e.preventDefault();
        if (extra.length) {
          const gone = new Set([widget.id, ...extra].filter((id) => !page.widgets.find((w) => w.id === id)?.locked));
          if (gone.size === 0) {
            toast.info("Locked visuals can't be deleted", "Unlock them in the Layers tab first.");
            return;
          }
          patchPage(page.id, (p) => ({ ...p, widgets: p.widgets.filter((w) => !gone.has(w.id)) }));
          setSelected(null);
          toast.info(`${gone.size} visuals removed`, "Press Ctrl+Z to bring them back.");
        } else removeWidget(widget.id);
      } else if (mod && e.key.toLowerCase() === "d") {
        e.preventDefault();
        duplicateWidget(widget.id);
      } else if (e.key === "Escape") setSelected(null);
      else if (e.key.startsWith("Arrow") && !isBarWidget(widget) && !widget.locked) {
        e.preventDefault();
        const l = { ...widget.layout };
        const cols = page.canvas?.columns ?? 24;
        const d = e.key === "ArrowLeft" ? [-1, 0] : e.key === "ArrowRight" ? [1, 0] : e.key === "ArrowUp" ? [0, -1] : [0, 1];
        if (extra.length && !e.shiftKey) {
          // nudge the whole selection together
          const ids = new Set([widget.id, ...extra]);
          patchPage(page.id, (p) => ({
            ...p,
            widgets: p.widgets.map((w) => (ids.has(w.id) && !isBarWidget(w) && !w.locked ? { ...w, layout: { ...w.layout, x: Math.max(0, Math.min(cols - w.layout.w, w.layout.x + d[0])), y: Math.max(0, w.layout.y + d[1]) } } : w)),
          }), `kbd-group`);
          return;
        }
        if (e.shiftKey) {
          l.w = Math.max(2, Math.min(cols - l.x, l.w + d[0]));
          l.h = Math.max(2, l.h + d[1]);
        } else {
          l.x = Math.max(0, Math.min(cols - l.w, l.x + d[0]));
          l.y = Math.max(0, l.y + d[1]);
        }
        patchWidget(widget.id, { layout: l }, `kbd-${widget.id}`);
      }
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [widget, page, history, removeWidget, duplicateWidget, patchWidget, doSave, extra, patchPage, toast, setSelected, selectedIds, restackSelection, toggleFlag, copyFormatCmd, pasteFormatCmd]);

  if (draftQ.isError) {
    return <div style={{ padding: 40 }}>Couldn’t open this report: {draftQ.error instanceof Error ? draftQ.error.message : "unknown error"}</div>;
  }
  if (!def || !page) {
    return (
      <div className="st-boot">
        <Loader2 className="ds-spin" size={22} /> Opening the designer…
      </div>
    );
  }

  const pages = [...def.pages].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  const outOfBounds = page.widgets.filter((w) => !isBarWidget(w) && w.layout.y + w.layout.h > rowsFor(page)).length;
  const SaveIcon = save === "saving" ? Loader2 : save === "error" ? CloudOff : save === "dirty" ? Cloud : CheckCircle2;

  return (
    <div className="st-root">
      <header className="st-top">
        <Button variant="ghost" icon={ArrowLeft} onClick={() => navigate(`${base}/reports/${reportId}`)} title="Back to the report">Back</Button>
        <input className="st-name" value={def.name} onChange={(e) => commit({ ...def, name: e.target.value }, "report-name")} aria-label="Report name" />
        <span className={`st-save st-save--${save}`} title={saveMsg}>
          <SaveIcon size={14} className={save === "saving" ? "ds-spin" : undefined} />
          {save === "saving" ? "Saving…" : save === "dirty" ? "Unsaved changes" : save === "error" ? "Not saved" : saveMsg || "All changes saved"}
        </span>
        <div className="ds-grow" />
        <PresenceBar peers={peers} def={def} note={remoteNote} />
        <GuideButton steps={guideSteps(def, guideFlags, openedTheme.current)} onAction={guideAction} />
        <DesignScoreToolbarItem mode={mode} reportId={reportId} definition={def} currentPageId={page.id} onSelectWidget={(pid, wid) => { setPageId(pid); setSelected(wid); }} />
        <span className="st-ai-wrap">
          <Button
            className="st-ai-btn"
            icon={Sparkles}
            aria-pressed={aiOpen}
            title="Ask AI to edit this dashboard (Ctrl+/)"
            onClick={() => { setAiOpen((o) => !o); dismissAiHint(); }}
          >
            Ask AI
          </Button>
          {aiHint && !aiOpen ? (
            <div className="st-ai-hint" role="note">
              <strong>New: Ask AI</strong>
              Describe a change in plain words - <em>"make the legend smaller"</em> - and it edits this dashboard for you. Always undoable.
              <button onClick={dismissAiHint} aria-label="Dismiss"><X size={13} /></button>
            </div>
          ) : null}
        </span>
        <Button variant="ghost" icon={History} onClick={() => setHistoryOpen(true)} title="Look back at published versions and saved drafts">History</Button>
        <Button variant="ghost" icon={Undo2} disabled={!history.canUndo} onClick={history.undo} title="Undo (Ctrl+Z)" aria-label="Undo" />
        <Button variant="ghost" icon={Redo2} disabled={!history.canRedo} onClick={history.redo} title="Redo (Ctrl+Y)" aria-label="Redo" />
        <Button icon={ExternalLink} onClick={previewAsTv}>Preview as TV</Button>
        <Button variant="primary" icon={Rocket} onClick={async () => { await doSave(); markGuide("save"); setPublishOpen(true); }}>Publish…</Button>
      </header>
      {save === "error" ? (
        <div className="st-alert">
          {saveMsg}{" "}
          <button className="st-link" onClick={() => window.location.reload()}>Reload</button>
        </div>
      ) : null}

      <div className={`st-body${aiOpen ? " has-ai" : ""}`}>
        <aside className={`st-left${flashVisuals ? " is-flash" : ""}`}>
          <div className="st-left__tabs" role="tablist">
            {([
              ["visuals", LayoutGrid, "Visuals"],
              ["data", Database, "Data"],
              ["pages", FileStack, "Pages"],
              ["layers", Layers, "Layers"],
            ] as const).map(([k, Icon, label]) => (
              <button key={k} role="tab" aria-selected={left === k} className="st-left__tab" onClick={() => setLeft(k)}>
                <Icon size={17} />
                <span>{label}</span>
              </button>
            ))}
          </div>
          {left === "visuals" ? <VisualsPanel onAdd={(t) => addVisual(t)} /> : null}
          {left === "data" ? (
            <DataPane
              mode={mode}
              def={def}
              getDef={() => latestDef.current}
              onChange={commit}
              activeDatasetId={dataDs ?? widget?.datasetId ?? null}
              onPickDataset={(id) => { setDataDs(id); if (widget && specFor(widget.type)?.needsData && !widget.datasetId) patchWidget(widget.id, { datasetId: id }); }}
              onAddField={widget ? (f) => bindField(widget.id, f) : null}
              onChartFromField={(f) => void chartFromField(f)}
              openPickerSignal={pickerSignal}
            />
          ) : null}
          {left === "layers" ? (
            <LayersPanel
              page={page}
              selectedIds={selectedIds}
              renameId={renameId}
              onRenameDone={() => setRenameId(null)}
              onSelect={(id, toggle) => (toggle ? toggleSelected(id) : setSelected(id))}
              onPatch={(id, patch, key) => patchWidget(id, patch, key)}
              onMove={(id, target, where) => patchPage(page.id, (p) => moveRelative(p, id, target, where))}
            />
          ) : null}
          {left === "pages" ? <PagesPanel def={def} activeId={page.id} onSelect={(id) => { setPageId(id); setSelected(null); }} onChange={commit} /> : null}
        </aside>

        <main className="st-center" onMouseDown={(e) => e.target === e.currentTarget && setSelected(null)}>
          <div className="st-pagetabs">
            {pages.length > 8 ? (
              <select className="st-pagejump" value={page.id} aria-label="Jump to page" onChange={(e) => { setPageId(e.target.value); setSelected(null); }}>
                {pages.map((p, i) => <option key={p.id} value={p.id}>{`${i + 1}. ${p.section ? `${p.section} › ` : ""}${p.name}`}</option>)}
              </select>
            ) : null}
            <div className="st-pagetabs-scroll">
              {pages.map((p) => (
                <button key={p.id} ref={p.id === page.id ? (el) => el?.scrollIntoView({ block: "nearest", inline: "nearest" }) : undefined} className={`st-pagetab${p.id === page.id ? " is-active" : ""}`} onClick={() => { setPageId(p.id); setSelected(null); }} title={p.section ? `${p.section} › ${p.name}` : p.name}>
                  {p.section ? <span className="ds-faint">{p.section} › </span> : null}
                  {p.name}
                </button>
              ))}
            </div>
            <Button size="sm" variant="ghost" icon={Plus} onClick={() => { const id = `page-${Math.random().toString(36).slice(2, 7)}`; commit({ ...def, pages: [...def.pages, { id, name: `Page ${def.pages.length + 1}`, order: def.pages.length, widgets: [], canvas: page.canvas, background: (page as any).background, filters: [] }] }); setPageId(id); }}>Page</Button>
            <span className="ds-grow" />
            <PreviewThemeToggle theme={canvasTheme} onChange={setCanvasTheme} label="Canvas" />
          </div>
          <ArrangeBar
            page={page}
            selectedIds={selectedIds}
            apply={(fn, key) => patchPage(page.id, fn, key)}
            onSelectAll={() => selectMany(page.widgets.filter((w) => !isBarWidget(w)).map((w) => w.id))}
            onRestack={restackSelection}
            onCopyFormat={copyFormatCmd}
            onPasteFormat={pasteFormatCmd}
            canPasteFormat={!!formatClip}
            onShortcuts={() => setShortcutsOpen(true)}
          />
          <StudioCanvas
            ref={canvasRef}
            reportId={reportId}
            source={mode}
            page={page}
            selectedId={selected}
            extraSelected={extra}
            onSelect={setSelected}
            onToggleSelect={toggleSelected}
            onSelectMany={selectMany}
            onColumnWidths={(id, widths) => {
              const w = page.widgets.find((x) => x.id === id);
              if (w) patchWidget(id, { display: setIn(w.display ?? {}, ["tablePresentation", "columnWidths"], widths) });
            }}
            scale={gridScale}
            onLayout={(id, l) => {
              const cur = page.widgets.find((w) => w.id === id);
              // Dragging one of several selected visuals moves them all by the same amount.
              if (cur && selectedIds.length > 1 && selectedIds.includes(id) && l.w === cur.layout.w && l.h === cur.layout.h) {
                const cols = page.canvas?.columns ?? 24;
                const dx = l.x - cur.layout.x;
                const dy = l.y - cur.layout.y;
                const ids = new Set(selectedIds.filter((sid) => !page.widgets.find((w) => w.id === sid)?.locked));
                const lo = Math.min(...page.widgets.filter((w) => ids.has(w.id)).map((w) => w.layout.x));
                const hi = Math.max(...page.widgets.filter((w) => ids.has(w.id)).map((w) => w.layout.x + w.layout.w));
                const sx = Math.max(-lo, Math.min(cols - hi, dx));
                const loY = Math.min(...page.widgets.filter((w) => ids.has(w.id)).map((w) => w.layout.y));
                const sy = Math.max(-loY, dy);
                patchPage(page.id, (p) => ({ ...p, widgets: p.widgets.map((w) => (ids.has(w.id) ? { ...w, layout: { ...w.layout, x: w.layout.x + sx, y: w.layout.y + sy } } : w)) }));
              } else patchWidget(id, { layout: l });
            }}
            onDropVisual={(t, at) => addVisual(t, at)}
            onDropField={bindField}
            onFramePage={(id) => setPageId(id)}
            onLoaded={() => canvasRef.current?.post({ type: "mrp:definition", definition: def })}
            theme={canvasTheme}
            onEmptyAddVisual={() => {
              setLeft("visuals");
              setFlashVisuals(false);
              window.requestAnimationFrame(() => setFlashVisuals(true));
              window.setTimeout(() => setFlashVisuals(false), 1500);
            }}
            onEmptySuggest={() => setSuggestOpen(true)}
            hasData={guideSteps(def, guideFlags, openedTheme.current)[0].done}
            onEmptyPickData={() => guideAction("data")}
            onEmptyStyle={() => guideAction("style")}
            onDropFieldNew={(f, at) => void chartFromField(f, at)}
            onEmptyTemplate={() => setTemplateOpen(true)}
          />
          <div className="st-status">
            <span>{page.widgets.length} visual{page.widgets.length === 1 ? "" : "s"} on this page</span>
            {outOfBounds ? <span className="st-warn">{outOfBounds} visual(s) extend below the screen — TVs won’t show them unless the page scrolls</span> : null}
            <span className="ds-grow" />
            <span className="ds-faint">Arrows move · Shift+arrows resize · Alt = no snapping · Del removes · Ctrl+D duplicates · Ctrl+Z undo · ? all shortcuts</span>
          </div>
        </main>

        <aside className="st-right">
          {widget ? (
            <PropertiesPanel
              key={selectedIds.length > 1 ? selKey : widget.id}
              mode={mode}
              widget={widget}
              multi={selectedIds.length > 1 ? page.widgets.filter((w) => selectedIds.includes(w.id)) : undefined}
              onChange={patchSelection}
              onReplay={() => canvasRef.current?.post({ type: "mrp:replay", widgetId: widget.id })}
              onDelete={() => removeWidget(widget.id)}
              onDuplicate={() => duplicateWidget(widget.id)}
              def={def}
              onApplyNumberFormatToPage={applyNumberFormatToPage}
              onApplyDisplayPatch={applyDisplayPatch}
            />
          ) : (
            <PageProperties def={def} page={page} previewMode={canvasTheme} onChange={commit} mode={mode} onSelectPage={(id) => { setPageId(id); setSelected(null); }} />
          )}
        </aside>
        <AiChatPanel
          open={aiOpen}
          mode={mode}
          reportId={reportId}
          pageName={page.name}
          selectedWidgets={page.widgets.filter((w) => selectedIds.includes(w.id)).map((w) => ({ id: w.id, type: w.type, title: w.title }))}
          getSnapshot={aiSnapshot}
          onApply={applyAi}
          onUndo={undoAi}
          onClose={() => setAiOpen(false)}
        />
      </div>

      <ShortcutsModal open={shortcutsOpen} onClose={() => setShortcutsOpen(false)} />
      <TemplatePickerModal open={templateOpen} onClose={() => setTemplateOpen(false)} onPick={applyTemplate} hasDataset={!!dataDs} />
      <Drawer open={suggestOpen} onClose={() => setSuggestOpen(false)} title="Suggest visuals from my data" width={520}>
        <SuggestPanel datasetId={dataDs ?? undefined} onAddWidget={(w) => { addReadyWidget(w as SWidget); }} onClose={() => setSuggestOpen(false)} />
      </Drawer>
      <Drawer open={historyOpen} onClose={() => setHistoryOpen(false)} title="Version history" width={940}>
        <VersionHistoryPanel
          api={api}
          reportId={reportId}
          queryScope={mode}
          theme={canvasTheme}
          onRestore={(definition, label) => {
            const restored = { ...(definition as unknown as SDef), reportId: (def as any).reportId ?? (definition as any).reportId } as SDef;
            commit(restored, uid("restore"));
            if (!restored.pages.some((p) => p.id === pageIdRef.current)) setPageId(restored.defaultPageId || restored.pages[0]?.id || "");
            setSelected(null);
            setHistoryOpen(false);
            toast.success(`Restored ${label} as your draft`, "Nothing is published. Ctrl+Z brings back what you had.");
          }}
        />
      </Drawer>
      <PublishDialog open={publishOpen} onClose={() => setPublishOpen(false)} reportId={reportId} isOwner={mode === "owner"} onPublished={() => qc.invalidateQueries({ queryKey: ["versions"] })} />
    </div>
  );
}
